<template>
  <Navigation withExit @back="emit('back')" @exit="emit('exit')" />
  <Title>{{ i18n(translations.title) }}</Title>

  <MessageForm :isLoading :mostUrgentTask @submit="sendMessage" />
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

import type { OrderlineOpenTask } from '~/api/seller-after-sales/types'
import { fetchAndGoToNextTask } from '~/scopes/customer-care/components/request.helpers'
import { useOrderlinePublicIdFromRoute } from '~/scopes/customer-care/pages/request/composables/useOrderlinePublicIdFromRoute'

import type { AttachmentUpload } from '../AttachmentsUpload/types'
import MessageForm from '../MessageForm/MessageForm.vue'
import Navigation from '../Navigation/Navigation.vue'
import Title from '../Title/Title.vue'

import translations from './SendMessageFlow.translations'

const i18n = useI18n()
const logger = useLogger()
const { openErrorToast, openSuccessToast } = useTheToast()
const $httpFetch = useHttpFetch()
const emit = defineEmits(['back', 'done', 'error', 'exit'])
const orderlinePublicId = useOrderlinePublicIdFromRoute()
const { FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION } = useRuntimeConfig().public

const { mostUrgentTask } = defineProps<{
  mostUrgentTask?: OrderlineOpenTask
}>()

const isLoading = ref(false)

async function sendMessage(values: {
  message: string
  attachments: AttachmentUpload[]
  isNextTaskChecked: boolean
}) {
  isLoading.value = true
  try {
    logger.debug(
      `Attachments from model: ${JSON.stringify(values.attachments)}`,
      {
        owners: ['bot-squad-seller-care-front'],
      },
    )

    const attachmentsToApi = values.attachments.map((attachment) => {
      return {
        handle: attachment.handle ?? '',
        filePath: attachment.filePath ?? '',
        bucketName: attachment.bucketName ?? '',
      }
    })

    await $httpFetch(
      '/seller-after-sales/api/v1/orderlines/:orderlinePublicId/message',
      {
        method: 'POST',
        pathParams: {
          orderlinePublicId: orderlinePublicId.value,
        },
        body: {
          message: values.message,
          attachments: [...attachmentsToApi],
        },
      },
    )

    if (values.isNextTaskChecked && FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION)
      fetchAndGoToNextTask(mostUrgentTask?.id).catch(() => {
        openErrorToast()
      })

    emit('done')
    openSuccessToast()
  } catch (error) {
    if (error instanceof Error) {
      openErrorToast()
      logger.error(error.message, {
        error,
        owners: ['bot-squad-seller-care-front'],
      })
    }
  } finally {
    isLoading.value = false
  }
}
</script>
